/**
 * The admin's view of the deposit release panel, without a container.
 *
 * Extracted from SettleDepositDialog so the same panel can be mounted in two
 * places — the dialog (for a decision the representative gave by phone) and
 * inline in the Move-out tab — without either growing its own copy of the
 * fetch, the loading state or the error handling. The refund maths and every
 * validation rule stay in DepositReleasePanel, which both entry points and the
 * representative's public page share.
 */

import { useEffect, useState } from "react";
import {
  fetchReleaseState,
  type DepositReleaseLogEvent,
  type ReleaseAuth,
  type ReleaseReviewState,
} from "@/lib/depositReleaseApi";
import { DepositReleasePanel } from "@/components/deposit/DepositReleasePanel";

export function AdminDepositRelease({
  settlementId,
  /** Skip the fetch entirely until the caller wants the panel on screen. */
  active = true,
  onReleased,
  onLog,
}: {
  settlementId: string | null;
  active?: boolean;
  /** Called once a decision is recorded, so the profile can refetch. */
  onReleased?: () => void;
  /** Forwarded from the panel, for the profile to audit-log. */
  onLog?: (event: DepositReleaseLogEvent) => void;
}) {
  const [state, setState] = useState<ReleaseReviewState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!active || !settlementId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setState(null);
    fetchReleaseState({ mode: "admin", settlementId })
      .then((next) => {
        if (!cancelled) setState(next);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : "Could not load the deposit"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, settlementId]);

  const auth: ReleaseAuth | null = settlementId
    ? { mode: "admin", settlementId }
    : null;

  if (!active) return null;

  return (
    <>
      {loading && (
        <div style={{ fontSize: 13, color: "#64748B", padding: "24px 0" }}>
          Loading…
        </div>
      )}
      {error && (
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: "#B91C1C",
            background: "#FEF2F2",
            border: "1px solid #FECACA",
            borderRadius: 12,
            padding: "12px 14px",
          }}
        >
          {error}
        </div>
      )}
      {state && auth && (
        <DepositReleasePanel
          auth={auth}
          state={state}
          locale="en"
          onState={(next) => {
            setState(next);
            if (next.released_at && !state.released_at) onReleased?.();
          }}
          onLog={onLog}
        />
      )}
    </>
  );
}
